import React, { useState, useEffect, useCallback } from 'react';
import { Link2, CheckCircle, AlertCircle, Users, BarChart3, Phone, RefreshCw, Settings, ArrowRight, Globe, Search, Download, Trash2, Plus, Activity, TrendingUp, Webhook, ArrowLeftRight } from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';
import { useToast } from '../../components/ui/Toast';

const GoHighLevelIntegration = () => {
  const toast = useToast();
  const [apiKey, setApiKey] = useState('');
  const [locationId, setLocationId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('connect');
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');

  const [syncConfig, setSyncConfig] = useState({
    contacts: true,
    opportunities: true,
    campaigns: false,
    appointments: true,
    conversations: false,
    automations: false,
    tags: true,
    pipelines: true,
  });

  const [syncStats, setSyncStats] = useState({
    contacts: 0, opportunities: 0, campaigns: 0,
    appointments: 0, conversations: 0, tags: 0
  });

  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [webhooks, setWebhooks] = useState([]);

  const mockContacts = [
    { id: 1, name: 'Sarah Johnson', email: 'sarah@techcorp.com', phone: '+1 555-234-5678', company: 'TechCorp Inc', status: 'synced', tags: ['hot-lead', 'enterprise'], lastActivity: '2 hours ago', score: 92 },
    { id: 2, name: 'Michael Chen', email: 'mchen@growth.io', phone: '+1 555-345-6789', company: 'Growth Agency', status: 'synced', tags: ['warm', 'smb'], lastActivity: '4 hours ago', score: 78 },
    { id: 3, name: 'Emily Rodriguez', email: 'emily@startupxyz.com', phone: '+1 555-456-7890', company: 'StartupXYZ', status: 'pending', tags: ['demo-scheduled'], lastActivity: '1 day ago', score: 65 },
    { id: 4, name: 'David Park', email: 'dpark@enterprise.co', phone: '+1 555-567-8901', company: 'Enterprise Co', status: 'synced', tags: ['hot-lead', 'ready-to-buy'], lastActivity: '30 min ago', score: 88 },
    { id: 5, name: 'Lisa Thompson', email: 'lisa@mediagroup.com', phone: '+1 555-678-9012', company: 'Media Group', status: 'error', tags: ['cold'], lastActivity: '3 days ago', score: 45 },
  ];

  const mockOpportunities = [
    { id: 1, name: 'TechCorp Enterprise Deal', contact: 'Sarah Johnson', value: 24000, stage: 'Proposal Sent', pipeline: 'Enterprise Sales', probability: 75, closeDate: '2026-03-15', status: 'synced' },
    { id: 2, name: 'Growth Agency Retainer', contact: 'Michael Chen', value: 8400, stage: 'Negotiation', pipeline: 'SMB Pipeline', probability: 60, closeDate: '2026-03-01', status: 'synced' },
    { id: 3, name: 'StartupXYZ Starter Plan', contact: 'Emily Rodriguez', value: 2400, stage: 'Demo Scheduled', pipeline: 'SMB Pipeline', probability: 40, closeDate: '2026-03-20', status: 'pending' },
    { id: 4, name: 'Enterprise Co Full Suite', contact: 'David Park', value: 48000, stage: 'Contract Review', pipeline: 'Enterprise Sales', probability: 90, closeDate: '2026-02-28', status: 'synced' },
  ];

  const mockPipelines = [
    { id: 1, name: 'Enterprise Sales', stages: ['Lead In', 'Discovery', 'Demo', 'Proposal Sent', 'Negotiation', 'Contract Review', 'Closed Won', 'Closed Lost'], deals: 12, value: 284000 },
    { id: 2, name: 'SMB Pipeline', stages: ['New Lead', 'Qualified', 'Demo Scheduled', 'Trial', 'Negotiation', 'Closed'], deals: 28, value: 96400 },
    { id: 3, name: 'Inbound Leads', stages: ['New', 'Contacted', 'Qualified', 'Converted'], deals: 45, value: 52000 },
  ];

  const mockActivity = [
    { id: 1, type: 'sync', message: 'Contact Sarah Johnson synced from GHL', time: '2 min ago', icon: RefreshCw, color: 'text-blue-500' },
    { id: 2, type: 'create', message: 'New opportunity created: Enterprise Co Full Suite', time: '15 min ago', icon: Plus, color: 'text-green-500' },
    { id: 3, type: 'update', message: 'Pipeline stage updated: TechCorp → Proposal Sent', time: '1 hour ago', icon: ArrowRight, color: 'text-orange-500' },
    { id: 4, type: 'webhook', message: 'Webhook triggered: contact.created', time: '2 hours ago', icon: Webhook, color: 'text-purple-500' },
    { id: 5, type: 'sync', message: 'Bulk sync completed: 142 contacts, 28 opportunities', time: '3 hours ago', icon: CheckCircle, color: 'text-green-500' },
    { id: 6, type: 'error', message: 'Sync failed for Lisa Thompson - duplicate email', time: '3 days ago', icon: AlertCircle, color: 'text-red-500' },
  ];

  const mockWebhooks = [
    { id: 1, event: 'contact.created', url: 'https://leadgenpr6367.builtwithrocket.new/api/webhooks/ghl', active: true, lastTriggered: '2 hours ago' },
    { id: 2, event: 'opportunity.updated', url: 'https://leadgenpr6367.builtwithrocket.new/api/webhooks/ghl', active: true, lastTriggered: '1 hour ago' },
    { id: 3, event: 'appointment.booked', url: 'https://leadgenpr6367.builtwithrocket.new/api/webhooks/ghl', active: false, lastTriggered: 'Never' },
  ];

  useEffect(() => {
    if (connected) {
      setContacts(mockContacts);
      setOpportunities(mockOpportunities);
      setPipelines(mockPipelines);
      setActivityLog(mockActivity);
      setWebhooks(mockWebhooks);
      setSyncStats({
        contacts: mockContacts?.length,
        opportunities: mockOpportunities?.length,
        campaigns: 3,
        appointments: 8,
        conversations: 24,
        tags: 12
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  const handleConnect = async (e) => {
    e?.preventDefault();
    if (!apiKey?.trim()) { setError('Please enter your GoHighLevel API key'); return; }
    setConnecting(true); setError('');
    try {
      await new Promise(r => setTimeout(r, 1800));
      if (apiKey?.length < 10) throw new Error('Invalid API key format. Please check your GoHighLevel API key.');
      setConnected(true);
      setActiveTab('dashboard');
      toast?.success('Successfully connected to GoHighLevel!', 'Connected ✓');
    } catch (err) {
      setError(err?.message || 'Failed to connect to GoHighLevel');
      toast?.error(err?.message || 'Failed to connect to GoHighLevel', 'Connection Failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (type = 'all') => {
    setSyncing(true); setSyncProgress(0);
    const steps = type === 'all' ? 5 : 1;
    for (let i = 1; i <= steps; i++) {
      await new Promise(r => setTimeout(r, 600));
      setSyncProgress(Math.round((i / steps) * 100));
    }
    setSyncing(false);
    setLastSync(new Date()?.toLocaleString());
    setSyncStats({ contacts: 142, opportunities: 28, campaigns: 12, appointments: 15, conversations: 47, tags: 34 });
    setActivityLog(prev => [{ id: Date.now(), type: 'sync', message: `${type === 'all' ? 'Full' : type} sync completed successfully`, time: 'Just now', icon: CheckCircle, color: 'text-green-500' }, ...prev]);
    toast?.success(`${type === 'all' ? 'Full' : type} sync completed successfully!`, 'Sync Complete ✓');
  };

  const handleAddWebhook = () => {
    if (!webhookUrl?.trim()) {
      toast?.warning('Please enter a webhook URL.', 'URL Required');
      return;
    }
    setWebhooks(prev => [...prev, { id: Date.now(), event: 'contact.created', url: webhookUrl, active: true, lastTriggered: 'Never' }]);
    setWebhookUrl('');
    setShowWebhookModal(false);
    toast?.success('Webhook added successfully!', 'Webhook Added ✓');
  };

  const handleToggleWebhook = (id) => {
    setWebhooks(prev => prev?.map(w => w?.id === id ? { ...w, active: !w?.active } : w));
    toast?.info('Webhook status updated.', 'Updated');
  };

  const handleDeleteWebhook = (id) => {
    setWebhooks(prev => prev?.filter(w => w?.id !== id));
    toast?.info('Webhook removed.', 'Removed');
  };

  const handleExportContacts = () => {
    const csv = ['Name,Email,Phone,Company,Status,Score', ...contacts?.map(c =>
      `${c?.name},${c?.email},${c?.phone},${c?.company},${c?.status},${c?.score}`
    )]?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'ghl_contacts.csv'; a?.click();
    URL.revokeObjectURL(url);
    toast?.success('Contacts exported to CSV!', 'Export Complete ✓');
  };

  const filteredContacts = contacts?.filter(c =>
    c?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    c?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    c?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const tabs = [
    { id: 'connect', label: 'Connect', icon: Link2 },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, disabled: !connected },
    { id: 'contacts', label: 'Contacts', icon: Users, disabled: !connected },
    { id: 'opportunities', label: 'Opportunities', icon: TrendingUp, disabled: !connected },
    { id: 'pipelines', label: 'Pipelines', icon: ArrowLeftRight, disabled: !connected },
    { id: 'sync', label: 'Sync Settings', icon: RefreshCw, disabled: !connected },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook, disabled: !connected },
    { id: 'activity', label: 'Activity', icon: Activity, disabled: !connected },
  ];

  const statusBadge = (status) => {
    const map = { synced: 'bg-green-50 text-green-700', pending: 'bg-yellow-50 text-yellow-700', error: 'bg-red-50 text-red-700' };
    return map?.[status] || 'bg-gray-50 text-gray-700';
  };

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Globe className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">GoHighLevel Integration</h1>
              <p className="text-muted-foreground text-sm">Bi-directional CRM sync & pipeline management</p>
            </div>
          </div>
          {connected && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Connected</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="flex overflow-x-auto border-b border-border">
            {tabs?.map(tab => {
              const TabIcon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => !tab?.disabled && setActiveTab(tab?.id)}
                  disabled={tab?.disabled}
                  className={`flex items-center space-x-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    tab?.disabled ? 'text-muted-foreground/40 cursor-not-allowed' :
                    activeTab === tab?.id ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50/50': 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* Connect Tab */}
            {activeTab === 'connect' && (
              <div className="max-w-lg mx-auto">
                {connected ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Connected to GoHighLevel</h3>
                    <p className="text-muted-foreground mb-6">Your account is synced and ready to use.</p>
                    <button
                      onClick={() => setActiveTab('dashboard')}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold transition-colors"
                    >
                      View Dashboard
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleConnect} className="space-y-5">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">Connect GoHighLevel</h3>
                      <p className="text-sm text-muted-foreground">Enter your GHL API credentials to enable bi-directional sync.</p>
                    </div>
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">API Key *</label>
                      <input
                        type="password"
                        value={apiKey}
                        onChange={e => setApiKey(e?.target?.value)}
                        placeholder="Enter your GoHighLevel API key"
                        className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Location ID (optional)</label>
                      <input
                        type="text"
                        value={locationId}
                        onChange={e => setLocationId(e?.target?.value)}
                        placeholder="Enter your GHL Location ID"
                        className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={connecting}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-xl font-semibold transition-colors"
                    >
                      {connecting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                      <span>{connecting ? 'Connecting...' : 'Connect GoHighLevel'}</span>
                    </button>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-xs text-blue-700">
                        <strong>How to get your API key:</strong> Log in to GoHighLevel → Settings → API → Copy your API key.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && connected && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.entries(syncStats)?.map(([key, val]) => (
                    <div key={key} className="bg-muted/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">{val}</p>
                      <p className="text-xs text-muted-foreground capitalize mt-1">{key}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Last sync: <span className="font-medium text-foreground">{lastSync || 'Never'}</span></p>
                  </div>
                  <button
                    onClick={() => handleSync('all')}
                    disabled={syncing}
                    className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-xl font-semibold text-sm transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    <span>{syncing ? 'Syncing...' : 'Sync All'}</span>
                  </button>
                </div>
                {syncing && (
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-orange-500 h-2 rounded-full transition-all" style={{ width: `${syncProgress}%` }} />
                  </div>
                )}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && connected && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  <button
                    onClick={handleExportContacts}
                    className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Contact</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase hidden md:table-cell">Company</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase hidden lg:table-cell">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase hidden lg:table-cell">Tags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredContacts?.map(contact => (
                        <tr key={contact?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium text-foreground">{contact?.name}</p>
                              <p className="text-xs text-muted-foreground">{contact?.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <p className="text-sm text-foreground">{contact?.company}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(contact?.status)}`}>
                              {contact?.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className="text-sm font-semibold text-foreground">{contact?.score}</span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <div className="flex flex-wrap gap-1">
                              {contact?.tags?.map(tag => (
                                <span key={tag} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{tag}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Opportunities Tab */}
            {activeTab === 'opportunities' && connected && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Opportunity</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase hidden md:table-cell">Value</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Stage</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase hidden lg:table-cell">Probability</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {opportunities?.map(opp => (
                      <tr key={opp?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">{opp?.name}</p>
                            <p className="text-xs text-muted-foreground">{opp?.contact}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm font-semibold text-foreground">${opp?.value?.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground">{opp?.stage}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-muted rounded-full h-1.5">
                              <div className="bg-orange-500 h-1.5 rounded-full" style={{ width: `${opp?.probability}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{opp?.probability}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(opp?.status)}`}>
                            {opp?.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pipelines Tab */}
            {activeTab === 'pipelines' && connected && (
              <div className="space-y-4">
                {pipelines?.map(pipeline => (
                  <div key={pipeline?.id} className="border border-border rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground">{pipeline?.name}</h3>
                        <p className="text-sm text-muted-foreground">{pipeline?.deals} deals · ${pipeline?.value?.toLocaleString()} total value</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pipeline?.stages?.map((stage, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">{stage}</span>
                          {i < pipeline?.stages?.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Sync Settings Tab */}
            {activeTab === 'sync' && connected && (
              <div className="space-y-5">
                <h3 className="font-semibold text-foreground">Sync Configuration</h3>
                <div className="space-y-3">
                  {Object.entries(syncConfig)?.map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between p-4 border border-border rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-foreground capitalize">{key}</p>
                        <p className="text-xs text-muted-foreground">Sync {key} between LeadGen Pro and GoHighLevel</p>
                      </div>
                      <button
                        onClick={() => setSyncConfig(prev => ({ ...prev, [key]: !prev?.[key] }))}
                        className={`relative w-11 h-6 rounded-full transition-colors ${val ? 'bg-orange-500' : 'bg-muted'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${val ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handleSync('all')}
                  disabled={syncing}
                  className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-xl font-semibold text-sm transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  <span>{syncing ? 'Syncing...' : 'Run Full Sync'}</span>
                </button>
              </div>
            )}

            {/* Webhooks Tab */}
            {activeTab === 'webhooks' && connected && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">Webhooks</h3>
                  <button
                    onClick={() => setShowWebhookModal(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Webhook
                  </button>
                </div>
                {webhooks?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Webhook className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No webhooks configured yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {webhooks?.map(wh => (
                      <div key={wh?.id} className="flex items-center justify-between p-4 border border-border rounded-xl">
                        <div>
                          <p className="text-sm font-medium text-foreground">{wh?.event}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{wh?.url}</p>
                          <p className="text-xs text-muted-foreground">Last triggered: {wh?.lastTriggered}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleWebhook(wh?.id)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${wh?.active ? 'bg-green-500' : 'bg-muted'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${wh?.active ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                          <button onClick={() => handleDeleteWebhook(wh?.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showWebhookModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowWebhookModal(false)} />
                    <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
                      <h3 className="font-semibold text-gray-900 mb-4">Add Webhook</h3>
                      <input
                        type="url"
                        value={webhookUrl}
                        onChange={e => setWebhookUrl(e?.target?.value)}
                        placeholder="https://your-endpoint.com/webhook"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 mb-4"
                      />
                      <div className="flex gap-3">
                        <button onClick={() => setShowWebhookModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                        <button onClick={handleAddWebhook} className="flex-1 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-semibold">Add Webhook</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && connected && (
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Activity Log</h3>
                {activityLog?.map(item => {
                  const ItemIcon = item?.icon;
                  return (
                    <div key={item?.id} className="flex items-start gap-3 p-3 border border-border rounded-xl">
                      <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                        <ItemIcon className={`w-4 h-4 ${item?.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground">{item?.message}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item?.time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default GoHighLevelIntegration;